import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MarkdownModule } from 'ngx-markdown';

import { FileService } from '../../../services/file.service';
import { MessageService } from '../../../services/message.service';
import { ChatDailyReportService } from '../../../services/chat-daily-report.service';
import { DailyReport, DailyReportResult } from '../../../types/Chatbot';

// Keep the freshly-fetched report's object URL alive long enough for the new
// tab to load it before releasing the memory.
const OBJECT_URL_TTL_MS = 60_000;

@Component({
  selector: 'app-chat-daily-report-card',
  imports: [CommonModule, MatButtonModule, MatIconModule, TranslateModule, MarkdownModule],
  templateUrl: './chat-daily-report-card.component.html',
  styleUrl: './chat-daily-report-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatDailyReportCardComponent {
  private readonly fileService = inject(FileService);
  private readonly reportService = inject(ChatDailyReportService);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly result = input.required<DailyReportResult>();
  public readonly dismissed = output<void>();

  public readonly isOpeningReport = signal(false);

  public readonly report = computed(() => {
    const result = this.result();
    return result.kind === 'merged' ? result.report : null;
  });

  // The ZIP filename is daily-reports-<date>.zip; show that date in the header.
  public readonly reportDate = computed(() => {
    const result = this.result();
    if (result.kind === 'merged') {
      return result.report.date;
    }
    return /(\d{4}-\d{2}-\d{2})/.exec(result.filename)?.[1] ?? null;
  });

  public openReport(): void {
    const result = this.result();
    if (result.kind !== 'merged' || this.isOpeningReport()) {
      return;
    }

    // The report URL now points at a JWT-protected API endpoint, so a plain
    // window.open() navigation (which can't carry the auth header) fails. Fetch
    // it through the authenticated HTTP stack — the interceptor attaches the
    // bearer token and the endpoint redirects to the stored file — then show
    // the resulting blob. The tab is opened synchronously, inside the click
    // handler, so it isn't treated as a blocked popup; it's pointed at the
    // object URL once the request resolves. (No `noopener` here: that would
    // null out the handle we need to redirect.)
    const report = result.report;
    const tab = window.open('', '_blank');
    this.isOpeningReport.set(true);

    this.reportService
      .downloadReport(report.url)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          this.isOpeningReport.set(false);
          const objectUrl = window.URL.createObjectURL(blob);
          if (tab) {
            tab.location.href = objectUrl;
          } else {
            // Popup was blocked — fall back to downloading the report.
            this.fileService.downloadBlob(blob, this.fallbackFilename(report));
          }
          setTimeout(() => window.URL.revokeObjectURL(objectUrl), OBJECT_URL_TTL_MS);
        },
        error: () => {
          this.isOpeningReport.set(false);
          tab?.close();
          this.messageService.showErrorMessage('chatbot.daily_report.open_failed');
        },
      });
  }

  private fallbackFilename(report: DailyReport): string {
    const fromUrl = report.url.split('?')[0].split('/').pop();
    return fromUrl?.includes('.') ? fromUrl : `daily-report-${report.date}.pdf`;
  }

  public downloadZip(): void {
    const result = this.result();
    if (result.kind !== 'zip') {
      return;
    }
    this.fileService.downloadBlob(result.blob, result.filename);
  }
}
